import React, { useMemo } from 'react';
import { Box, Typography, Divider, Paper, Alert } from '@mui/material';
import { DebugRequestPanelProps } from './types';
import DebugRequestSection from './DebugRequestSection';
import CopyButton from './CopyButton';

/**
 * Main panel component for displaying debug request/response data
 */
const DebugRequestPanel: React.FC<DebugRequestPanelProps> = ({
  debugInfo,
  title = 'Debug Information',
}) => {
  const { main, other } = debugInfo;

  const totalRequests = main.length + other.length;
  const hasErrors = useMemo(
    () =>
      [...main, ...other].some(
        (req) => req.error || (req.responseStatus && req.responseStatus >= 400),
      ),
    [main, other],
  );

  if (totalRequests === 0) {
    return (
      <Alert severity="info" sx={{ m: 2 }}>
        No debug information captured.
      </Alert>
    );
  }

  return (
    <Box sx={{ p: 2 }}>
      {/* Header */}
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          mb: 2,
        }}
      >
        <Box>
          <Typography variant="h6">{title}</Typography>
          <Typography variant="body2" color="text.secondary">
            {totalRequests} request{totalRequests !== 1 ? 's' : ''} captured
            {hasErrors && (
              <Typography component="span" color="error.main" sx={{ ml: 1 }}>
                (contains errors)
              </Typography>
            )}
          </Typography>
        </Box>
        <CopyButton
          content={debugInfo}
          tooltip="Copy all debug data"
          size="medium"
        />
      </Box>

      {/* Main requests section */}
      {main.length > 0 && (
        <Paper variant="outlined" sx={{ mb: 3, overflow: 'hidden' }}>
          <Box
            sx={{
              p: 1.5,
              backgroundColor: 'primary.50',
              borderBottom: 1,
              borderColor: 'divider',
            }}
          >
            <Typography
              variant="subtitle1"
              fontWeight="bold"
              color="primary.main"
            >
              Primary Requests ({main.length})
            </Typography>
            <Typography variant="caption" color="text.secondary">
              Publish requests and MLS responses
            </Typography>
          </Box>
          <Box sx={{ p: 1.5 }}>
            {main.map((request, index) => (
              <DebugRequestSection
                key={request.id || index}
                request={request}
                defaultExpanded={index === 0}
              />
            ))}
          </Box>
        </Paper>
      )}

      {/* Other requests section */}
      {other.length > 0 && (
        <Paper variant="outlined" sx={{ overflow: 'hidden' }}>
          <Box
            sx={{
              p: 1.5,
              backgroundColor: 'grey.50',
              borderBottom: 1,
              borderColor: 'divider',
            }}
          >
            <Typography variant="subtitle1" fontWeight="bold">
              Supporting Requests ({other.length})
            </Typography>
            <Typography variant="caption" color="text.secondary">
              Saves, media, agents, and other operations
            </Typography>
          </Box>
          <Box sx={{ p: 1.5 }}>
            {other.map((request, index) => (
              <DebugRequestSection
                key={request.id || index}
                request={request}
                defaultExpanded={false}
              />
            ))}
          </Box>
        </Paper>
      )}
    </Box>
  );
};

export default React.memo(DebugRequestPanel);
